import React, { useCallback, useEffect, useRef, useState } from "react";
import cn from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { actions as mainActions } from "../../actions/main";
import { Logo } from "../header/logo";
import { ReactComponent as HomeIcon } from "../../media/icons/sidebar/home.svg";
import { ReactComponent as InboxIcon } from "../../media/icons/sidebar/inbox.svg";
import { ReactComponent as ReferralsIcon } from "../../media/icons/sidebar/referrals.svg";
import { ReactComponent as ProfileIcon } from "../../media/icons/sidebar/profile.svg";
import { ReactComponent as PartnersIcon } from "../../media/icons/sidebar/partners.svg";
import { ReactComponent as ListingsIcon } from "../../media/icons/sidebar/listings.svg";
import { ReactComponent as TeamIcon } from "../../media/icons/sidebar/team.svg";
import { ReactComponent as PaymentIcon } from "../../media/icons/sidebar/payments.svg";
import { ReactComponent as NotificationIcon } from "../../media/icons/sidebar/notification.svg";
import { COMMUNITY_REFERRALS_V2, PAID_REFERRALS_V2, PRO_BONO_V2, TEAMS_FEATURE_ENABLED } from "../../constants/featureFlags";
import { getPaidLeadsBasePath } from "../payPerLead/basePath";
import { getProBonoBasePath } from "../proBono/basePath";
import styles from "./mainSidebar.module.css";

// Below this width the sidebar is a drawer opened from the header's hamburger; from 1320px it is part of the page.
const DRAWER_QUERY = "(max-width: 1319px)";
const FOCUSABLE = "a[href], button:not([disabled]), [tabindex]:not([tabindex='-1'])";

/** Whether Tab can reach `element`: neither it nor a parent up to `root` is display:none or visibility:hidden. */
const isTabbable = (element, root) => {
  for (let node = element; node && node !== root.parentNode; node = node.parentElement) {
    const style = window.getComputedStyle(node);

    if (style.display === "none" || style.visibility === "hidden") {
      return false;
    }
  }

  return true;
};

/** Whether the viewport is drawer-sized, or null where matchMedia is missing (the drawer then keeps its old behaviour). */
const useIsDrawer = () => {
  const [isDrawer, setIsDrawer] = useState(() => (window.matchMedia ? window.matchMedia(DRAWER_QUERY).matches : null));

  useEffect(() => {
    if (!window.matchMedia) {
      return undefined;
    }

    const query = window.matchMedia(DRAWER_QUERY);
    const update = () => setIsDrawer(query.matches);

    update();
    query.addEventListener("change", update);

    return () => query.removeEventListener("change", update);
  }, []);

  return isDrawer;
};

const MainSidebar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isActiveMobileMenu = useSelector(state => state.main.isActiveMobileMenu);
  const newMessages = useSelector(state => state.account.profile?.new_messages);
  const normalizedNewMessages = Number.isFinite(Number(newMessages)) ? Number(newMessages) : 0;
  const toggleMobileMenu = useCallback(isActive => dispatch(mainActions.toggleMobileMenu(isActive)), [dispatch]);

  const location = useLocation();
  const isSavedPage = location.pathname === "/saved";
  const activeSidebar = isActiveMobileMenu ? styles.sidebarActive : "";
  const overlaySidebar = isActiveMobileMenu ? styles.overlayActive : "";

  const handleCloseSidebar = () => {
    if (isActiveMobileMenu) {
      toggleMobileMenu(false);
    }
  };

  // GROWTH-7718: the open drawer is a modal dialog: Esc closes it, Tab stays inside it, the page behind does not scroll,
  // and focus goes back to what opened it. Growing past the drawer widths closes it (the sidebar is then in the page).
  const isDrawer = useIsDrawer();
  const isDrawerOpen = Boolean(isDrawer && isActiveMobileMenu);
  const drawerRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    if (isDrawer === false && isActiveMobileMenu) {
      toggleMobileMenu(false);
    }
  }, [isDrawer, isActiveMobileMenu, toggleMobileMenu]);

  useEffect(() => {
    if (!isDrawerOpen) {
      return undefined;
    }

    // Safari and Firefox on a Mac do not focus a button when it is clicked, so the opener can be the body: the menu button then.
    const active = document.activeElement;
    const opener = active && active !== document.body ? active : document.querySelector("[aria-controls='main-sidebar']");
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    if (closeRef.current) {
      closeRef.current.focus();
    }

    const handleKeyDown = event => {
      if (event.key === "Escape") {
        toggleMobileMenu(false);
        return;
      }

      if (event.key !== "Tab" || !drawerRef.current) {
        return;
      }

      // Only what Tab can reach: a hidden last link would let Tab leave the drawer and Shift+Tab stick on the first.
      const stops = [...drawerRef.current.querySelectorAll(FOCUSABLE)].filter(stop => isTabbable(stop, drawerRef.current));
      const first = stops[0];
      const last = stops[stops.length - 1];

      if (!drawerRef.current.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;

      if (opener && opener.isConnected && typeof opener.focus === "function") {
        opener.focus();
      }
    };
  }, [isDrawerOpen, toggleMobileMenu]);

  return (
    <>
      <div
        id="main-sidebar"
        ref={drawerRef}
        className={cn(styles.sidebar, activeSidebar)}
        {...(isDrawerOpen ? { role: "dialog", "aria-modal": "true", "aria-label": t("dashboard_main_menu_label") } : {})}
      >
        <button
          ref={closeRef}
          type="button"
          className={styles.drawerClose}
          onClick={handleCloseSidebar}
          aria-label={t("dashboard_main_menu_close")}
        >
          <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
            <path d="M4 4l12 12M16 4L4 16" />
          </svg>
        </button>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- not a control: the click is the logo link's, bubbling up */}
        <div className={styles.logo} onClick={handleCloseSidebar}>
          <Logo />
        </div>

        <NavLink
          to="/"
          onClick={handleCloseSidebar}
          className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive || isSavedPage })}
        >
          <HomeIcon />
          {t("dashboard_nav_feed")}
        </NavLink>

        <NavLink
          to="/profile"
          onClick={handleCloseSidebar}
          className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}
        >
          <ProfileIcon />
          {t("dashboard_nav_profile")}
        </NavLink>

        <NavLink to="/inbox" onClick={handleCloseSidebar} className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}>
          <InboxIcon />
          {t("dashboard_nav_inbox")}

          <span className={cn(styles.inbox, { [styles.inboxActive]: normalizedNewMessages > 0 })}>{normalizedNewMessages}</span>
        </NavLink>

        <NavLink
          to="/referrals"
          onClick={handleCloseSidebar}
          className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}
        >
          <ReferralsIcon />
          {t("dashboard_nav_referralrequests")}
        </NavLink>

        {/* GROWTH-8125: the link returns with the redesigned Community Referrals page; it stays hidden while COMMUNITY_REFERRALS_V2 is off. */}
        {COMMUNITY_REFERRALS_V2 && (
          <NavLink
            to="/community-referrals"
            onClick={handleCloseSidebar}
            className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}
          >
            <ReferralsIcon />
            {t("dashboard_nav_community_referrals")}
          </NavLink>
        )}
        {/* we will use this link
        <NavLink activeClassName={styles.activeLink} className={styles.link} to="/question-answer" onClick={handleCloseSidebar}>
          <ReferralsIcon />
          {t("dashboard_nav_questions")}
        </NavLink>
        */}

        <span className={styles.divider} />
        <NavLink
          to="/my-organizations"
          onClick={handleCloseSidebar}
          className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}
        >
          <ListingsIcon />
          Organization Listings
        </NavLink>

        <NavLink
          to="/my-events"
          onClick={handleCloseSidebar}
          className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}
        >
          <ListingsIcon />
          Event Listings
        </NavLink>

        <NavLink
          to="/my-jobs"
          onClick={handleCloseSidebar}
          className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}
        >
          <ListingsIcon />
          Job Listings
        </NavLink>

        <span className={styles.divider} />
        <NavLink
          to={getPaidLeadsBasePath()}
          onClick={handleCloseSidebar}
          className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}
        >
          <ReferralsIcon />
          {t(PAID_REFERRALS_V2 ? "dashboard_nav_paid_referrals" : "dashboard_nav_leads")}
        </NavLink>

        {/* GROWTH-7941: the link returns with the Pro Bono Referrals page; it stays hidden while PRO_BONO_V2 is off. */}
        {PRO_BONO_V2 && (
          <NavLink
            to={getProBonoBasePath()}
            onClick={handleCloseSidebar}
            className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}
          >
            <ReferralsIcon />
            {t("dashboard_nav_probono_referrals")}
          </NavLink>
        )}

        <span className={styles.divider} />

        <NavLink
          to="/account"
          onClick={handleCloseSidebar}
          className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}
        >
          <ProfileIcon />
          {t("dashboard_nav_account")}
        </NavLink>

        <NavLink
          to="/affiliate"
          onClick={handleCloseSidebar}
          className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}
        >
          <PartnersIcon />
          {t("dashboard_nav_partners")}
        </NavLink>

        <NavLink
          to="/manage-notification"
          onClick={handleCloseSidebar}
          className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}
        >
          <NotificationIcon />
          {t("dashboard_nav_notifications")}
        </NavLink>

        <NavLink
          to="/payments"
          onClick={handleCloseSidebar}
          className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}
        >
          <PaymentIcon />
          {t("dashboard_nav_payments")}
        </NavLink>

        {TEAMS_FEATURE_ENABLED ? (
          <NavLink
            to="/teams"
            onClick={handleCloseSidebar}
            className={({ isActive }) => cn(styles.link, { [styles.activeLink]: isActive })}
          >
            <TeamIcon />
            {t("dashboard_nav_team")}
          </NavLink>
        ) : null}
      </div>

      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- a pointer-only overlay; the hamburger and the links are the keyboard paths */}
      <div className={cn(styles.overlay, overlaySidebar)} onClick={handleCloseSidebar} />
    </>
  );
};

export default React.memo(MainSidebar);
